// Absolute URLs for raster shell backgrounds exported from the legacy MOR-392-era
// Figma Design-system Shell component, node 473:415. Visual provenance only; do
// not import from that file. Satori only renders images fetched via absolute
// http(s) URLs — relative `/public` paths fail.

// Shell backgrounds. Server renders (Satori) inline the PNG as a base64
// `data:` URI so it never makes a per-render HTTP fetch for the (up to ~450KB)
// background — the byte read + cache happens in the SERVER-ONLY
// `shell-background-fs.ts`, injected via `setShellBackgroundResolver()` (this
// module is bundled client-side too — for the in-app preview — so it must stay
// free of `node:` imports, same constraint as `register-image-url.ts`).
// Without an injected resolver (client preview), we return the absolute CDN
// URL, which Satori/the browser fetch as before.

import { resolveOgOrigin } from '../../get-pattern-url';
import type { OgShellVariant } from '../../registry';

const SHELL_BACKGROUND_PATH: Record<OgShellVariant, string> = {
  // Figma exports are misnamed on disk — filenames do not match visuals.
  default: '/images/og/shells/celebration.png',
  celebration: '/images/og/shells/default.png',
  data: '/images/og/shells/data.png',
  mono: '/images/og/shells/mono.png',
  gift: '/images/og/shells/default.png',
};

const RESOLVER_KEY = '__morfi_og_shell_bg_resolver__' as const;
/** Maps a `/images/...` public path to an inlined `data:` URI, or null. */
type ShellBackgroundResolver = (publicPath: string) => string | null;
interface ResolverHost {
  [RESOLVER_KEY]?: ShellBackgroundResolver;
}

/** Server-only: bind the FS-backed data-URI resolver (see
 *  `shell-background-fs.ts`). Called once from `renderOg()`. */
export function setShellBackgroundResolver(resolver: ShellBackgroundResolver | null): void {
  const host = globalThis as ResolverHost;
  if (resolver === null) {
    delete host[RESOLVER_KEY];
    return;
  }
  host[RESOLVER_KEY] = resolver;
}

export function getShellBackgroundUrl(variant: OgShellVariant): string {
  const publicPath = SHELL_BACKGROUND_PATH[variant];
  const resolver = (globalThis as ResolverHost)[RESOLVER_KEY];
  if (resolver !== undefined) {
    const dataUri = resolver(publicPath);
    if (dataUri !== null) return dataUri;
  }
  return `${resolveOgOrigin()}${publicPath}`;
}
